import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
  Alert,
  Divider,
  styled,
  Checkbox
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Hotel as HotelIcon,
  DateRange as DateRangeIcon,
  CurrencyRupee as RupeeIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import useCustomToast from '../hooks/useCustomToast';
import RAZORPAY_CONFIG, { validateRazorpayConfig } from '../config/razorpay';
import { decryptHotelId } from '../utils/payInvoiceUtils';
import PayInvoiceDetails from './PayInvoiceDetails';

const SECRET_KEY = 'tabtrips_hotel_secret';

// Fallback decryption function
const decryptHotelIdFallback = (encryptedHotelId) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedHotelId, SECRET_KEY);
    const result = bytes.toString(CryptoJS.enc.Utf8);
    return result && result.trim() !== '' ? result : null;
  } catch {
    return null;
  }
};

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(window.Razorpay);
    document.body.appendChild(script);
  });
};

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  },
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  background: 'linear-gradient(135deg, #FF7700 0%, #FFA040 100%)',
  color: 'white',
  boxShadow: '0 4px 20px rgba(255, 119, 0, 0.3)',
}));

const PayInvoice = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { successToast, errorToast } = useCustomToast();
  
  // State management
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadDate, setDownloadDate] = useState(dayjs());
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [showReservationDetails, setShowReservationDetails] = useState(false);
  const [hotelId, setHotelId] = useState(null);
  const [hotelName, setHotelName] = useState('');
  
  // Get encrypted hotel ID from URL parameters
  useEffect(() => {
    const fullSearchString = window.location.search;
    const urlParams = new URLSearchParams(window.location.search);
    let encryptedHotelId = urlParams.get('hotel');
    
    if (!encryptedHotelId || encryptedHotelId.includes(' ')) {
      const match = fullSearchString.match(/[?&]hotel=([^&]+)/);
      if (match) {
        encryptedHotelId = decodeURIComponent(match[1]);
      }
    }
    
    if (encryptedHotelId) {
      let decryptedHotelId = decryptHotelId(encryptedHotelId);
      if (!decryptedHotelId) {
        decryptedHotelId = decryptHotelIdFallback(encryptedHotelId);
      }
      
      if (decryptedHotelId && decryptedHotelId.trim() !== '') {
        setHotelId(decryptedHotelId);
        fetchHotelName(decryptedHotelId);
      } else {
        setError('Invalid hotel access token');
      }
    } else {
      setError('Hotel access token is required');
    }
  }, []);

  // Fetch hotel name
  const fetchHotelName = async (hotelId) => {
    try {
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHotelInfo',
        { hotel_id: hotelId },
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      if (response.data.status && response.data.hotel) {
        setHotelName(response.data.hotel.hotel_name || 'Hotel');
      }
    } catch (err) {
      setHotelName('Hotel');
    }
  };

  // Fetch reservations when date changes
  useEffect(() => {
    if (hotelId && showReservationDetails) {
      fetchReservations();
    }
  }, [selectedDate, hotelId, showReservationDetails]);

  // Auto-fetch invoice data on component mount
  useEffect(() => {
    if (hotelId && !showReservationDetails && !showInvoiceDetails) {
      fetchInvoiceData();
    }
  }, [hotelId]);

  const fetchReservations = async () => {
    if (!hotelId) {
      errorToast('Hotel ID not found');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const monthYear = selectedDate.format('MM/YYYY');
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getReservation',
        {
          hotel_id: hotelId,
          month: monthYear
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.status) {
        setReservations(response.data.data || []);
      } else {
        setError(response.data.message || 'No reservations found');
        setReservations([]);
      }
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError('Failed to fetch reservation details');
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceData = async () => {
    if (!hotelId) {
      errorToast('Hotel ID not found');
      return;
    }

    setInvoiceLoading(true);
    
    try {
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getInvoice',
        { hotel_id: hotelId },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.status) {
        setInvoiceData(response.data.invoice || null);
        if (response.data.invoice) {
          setShowInvoiceDetails(true);
        }
      } else {
        setInvoiceData(null);
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      errorToast('Failed to fetch invoice data');
      setInvoiceData(null);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!hotelId) {
      errorToast('Hotel ID not found');
      return;
    }

    setDownloading(true);
    
    try {
      const monthYear = downloadDate.format('MM/YYYY');
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/downloadReservationCSV',
        {
          hotel_id: hotelId,
          month: monthYear
        },
        { 
          headers: { 'Content-Type': 'application/json' },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reservations_${monthYear.replace('/', '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      successToast('Reservation details downloaded successfully');
      setDownloadDialogOpen(false);
    } catch (err) {
      console.error('Error downloading reservations:', err);
      errorToast('Failed to download reservation details');
    } finally {
      setDownloading(false);
    }
  };

  const calculateTotals = () => {
    return reservations.reduce((acc, reservation) => {
      acc.totalAmount += parseFloat(reservation.final_amount) || 0;
      acc.totalCommission += parseFloat(reservation.tabtrips_commission) || 0;
      acc.totalNights += parseInt(reservation.total_nights) || 0;
      return acc;
    }, { totalAmount: 0, totalCommission: 0, totalNights: 0 });
  };

  const formatCurrency = (amount) => {
    const value = parseFloat(amount) || 0;
    return `₹${value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const getCurrencyCode = () => {
    return 'INR';
  };

  const handlePayAllInvoices = async () => {
    if (!invoiceData || !invoiceData.length) {
      errorToast('No invoices found to pay');
      return;
    }

    const allInvoiceIds = invoiceData
      .filter(invoice => invoice.status !== 'Paid')
      .map(invoice => invoice.id || invoice.invoice_id);

    if (allInvoiceIds.length === 0) {
      errorToast('All invoices are already paid');
      return;
    }

    try {
      if (!validateRazorpayConfig()) {
        errorToast('Payment system configuration incomplete');
        return;
      }

      if (!hotelId) {
        errorToast('Hotel ID not found');
        return;
      }

      const generateOrderPayload = {
        hotel_id: hotelId,
        selected_invoice: allInvoiceIds,
        currency: getCurrencyCode()
      };

      const generateOrderResponse = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/generateInvoiceOrder',
        generateOrderPayload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (!generateOrderResponse.data.status) {
        throw new Error(generateOrderResponse.data.message || 'Failed to generate invoice order');
      }

      const orderData = generateOrderResponse.data.order;
      const invoiceIdsFromResponse = generateOrderResponse.data.invoice_ids;

      const Razorpay = await loadRazorpayScript();
      
      const options = {
        key: RAZORPAY_CONFIG.KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency || getCurrencyCode(),
        name: 'TabTrips',
        description: 'All Invoice Payments',
        order_id: orderData.id,
        handler: async function (response) {
          try {
            const submitPaymentPayload = {
              hotel_id: hotelId,
              invoice_ids: invoiceIdsFromResponse,
              order_id: orderData.id,
              razorpay_signature: response.razorpay_signature,
              payment_id: response.razorpay_payment_id
            };

            const submitPaymentResponse = await axios.post(
              'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/submitPaymentRecord',
              submitPaymentPayload,
              { headers: { 'Content-Type': 'application/json' } }
            );

            if (submitPaymentResponse.data.status) {
              successToast('Payment successful! All invoices have been marked as paid.');
              window.location.reload();
            } else {
              errorToast(submitPaymentResponse.data.message || 'Failed to update payment record');
            }
          } catch (error) {
            errorToast('Payment successful but failed to update records. Please contact support.');
          }
        },
        prefill: {
          name: 'Hotel',
          email: 'hotel@example.com',
          contact: ''
        },
        theme: {
          color: '#4CAF50'
        },
        modal: {
          ondismiss: function() {
            // Payment modal dismissed
          }
        }
      };

      const rzp = new Razorpay(options);
      rzp.open();
      
    } catch (error) {
      errorToast(error.message || 'Failed to process payment');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleBackToMain = () => {
    setShowInvoiceDetails(false);
    setShowReservationDetails(false);
    setInvoiceData(null);
  };

  // Show error if hotel ID is invalid
  if (error && !hotelId) {
    return (
      <Box sx={{ 
        p: { xs: 2, md: 4 },
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 3
      }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please contact support if you believe this is an error.
        </Typography>
      </Box>
    );
  }

  // Show invoice details if available
  if (showInvoiceDetails && invoiceData) {
    return (
      <PayInvoiceDetails 
        invoiceData={invoiceData} 
        onBack={handleBackToMain}
        onViewReservations={() => {
          setShowInvoiceDetails(false);
          setShowReservationDetails(true);
        }}
        hotelId={hotelId}
        hotelName={hotelName}
      />
    );
  }

  // Show reservation details
  if (showReservationDetails) {
    const totals = calculateTotals();

    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: { xs: 2, sm: 0 },
          mb: 3 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ReceiptIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: '#FF7700' }} />
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} sx={{ color: '#2E4057' }}>
                Reservation Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedDate.format('MMMM YYYY')}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => setDownloadDialogOpen(true)}
              sx={{
                borderColor: '#FF7700',
                color: '#FF7700',
                borderRadius: 3,
                '&:hover': {
                  background: 'rgba(255, 119, 0, 0.1)',
                  borderColor: '#E56900',
                }
              }}
            >
              Download CSV
            </Button>
            
            <Button
              variant="outlined"
              onClick={handleBackToMain}
              sx={{ borderRadius: 3 }}
            >
              Back to Invoice
            </Button>
          </Box>
        </Box>

        {/* Month Selection */}
        <StyledCard sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <CalendarIcon sx={{ color: '#FF7700' }} />
              <Typography variant="h6" fontWeight={600}>
                Select Month & Year
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  views={['month', 'year']}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 200 }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
          </CardContent>
        </StyledCard>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6} md={6}>
            <SummaryCard>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <RupeeIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  <Typography variant={isMobile ? "body2" : "h6"} fontWeight={600}>
                    Total Revenue
                  </Typography>
                </Box>
                <Typography variant={isMobile ? "h6" : "h4"} fontWeight={700}>
                  {formatCurrency(totals.totalAmount)}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handlePayAllInvoices}
                    disabled={totals.totalAmount <= 0}
                    sx={{
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      fontWeight: 600,
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      '&:hover': {
                        backgroundColor: '#45a049',
                      },
                      '&:disabled': {
                        backgroundColor: '#cccccc',
                        color: '#666666',
                      }
                    }}
                  >
                    PAY NOW
                  </Button>
                </Box>
              </CardContent>
            </SummaryCard>
          </Grid>
          
          <Grid item xs={6} sm={6} md={6}>
            <StyledCard>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingUpIcon sx={{ color: '#FF7700', fontSize: { xs: 20, sm: 24 } }} />
                  <Typography variant={isMobile ? "body2" : "h6"} fontWeight={600} color="text.primary">
                    Commission
                  </Typography>
                </Box>
                <Typography variant={isMobile ? "h6" : "h4"} fontWeight={700} color="#FF7700">
                  {formatCurrency(totals.totalCommission)}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Reservations Table */}
        <StyledCard>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Reservation Details - {selectedDate.format('MMMM YYYY')}
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : reservations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No reservations found for {selectedDate.format('MMMM YYYY')}
                </Typography>
              </Box>
            ) : (
              <>
                {isMobile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {reservations.map((reservation) => (
                      <Card key={reservation.booking_id} sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" fontWeight={600} color="#2E4057">
                            #{reservation.booking_id}
                          </Typography>
                          <Chip
                            label={reservation.booking_status}
                            color={getStatusColor(reservation.booking_status)}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PersonIcon sx={{ fontSize: 16, color: '#FF7700' }} />
                          <Typography variant="body1" fontWeight={500}>
                            {reservation.guest_name}
                          </Typography>
                        </Box>
                        
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Check In</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {dayjs(reservation.check_in_date).format('DD/MM/YYYY')}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Check Out</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {dayjs(reservation.check_out_date).format('DD/MM/YYYY')}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Nights</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {reservation.total_nights}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Amount</Typography>
                            <Typography variant="body2" fontWeight={600} color="#2E4057">
                              {formatCurrency(reservation.final_amount)}
                            </Typography>
                          </Grid>
                        </Grid>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1.5,
                          background: 'rgba(255, 119, 0, 0.1)',
                          borderRadius: 1
                        }}>
                          <Typography variant="body2" fontWeight={600} color="#FF7700">
                            Commission: {formatCurrency(reservation.tabtrips_commission)}
                          </Typography>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ background: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Booking ID</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Guest Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Check In</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Check Out</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Nights</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Commission</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reservations.map((reservation) => (
                          <TableRow key={reservation.booking_id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                #{reservation.booking_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon sx={{ fontSize: 16, color: '#FF7700' }} />
                                <Typography variant="body2">
                                  {reservation.guest_name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {dayjs(reservation.check_in_date).format('DD/MM/YYYY')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {dayjs(reservation.check_out_date).format('DD/MM/YYYY')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {reservation.total_nights}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} color="#2E4057">
                                {formatCurrency(reservation.final_amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} color="#FF7700">
                                {formatCurrency(reservation.tabtrips_commission)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={reservation.booking_status}
                                color={getStatusColor(reservation.booking_status)}
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </CardContent>
        </StyledCard>

        {/* Download Dialog */}
        <Dialog 
          open={downloadDialogOpen} 
          onClose={() => setDownloadDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, color: '#2E4057' }}>
            Download Reservation Details
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the month and year for which you want to download the reservation details.
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={downloadDate}
                onChange={(newValue) => setDownloadDate(newValue)}
                views={['month', 'year']}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Select Month & Year"
                    variant="outlined"
                  />
                )}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDownloadDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDownload}
              variant="contained"
              disabled={downloading}
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              sx={{
                background: 'linear-gradient(90deg, #FF7700 0%, #FFA040 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(90deg, #E56900 0%, #FF7700 100%)',
                }
              }}
            >
              {downloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Main invoice page
  return (
    <Box 
      sx={{ 
        p: { xs: 2, md: 4 },
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReceiptIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: '#FF7700' }} />
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} sx={{ color: '#2E4057' }}>
              Invoice Payment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hotelName ? `${hotelName}` : 'View and pay your invoices'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Loading State */}
      {invoiceLoading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh',
          flexDirection: 'column',
          gap: 3
        }}>
          <CircularProgress size={60} sx={{ color: '#FF7700' }} />
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
            Loading Invoice Data...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we fetch your invoice information
          </Typography>
        </Box>
      ) : (
        /* No Invoice Data State */
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          textAlign: 'center',
          gap: 3
        }}>
          {/* Empty State Icon */}
          <Box sx={{
            width: { xs: 120, sm: 150, md: 180 },
            height: { xs: 120, sm: 150, md: 180 },
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,119,0,0.1) 0%, rgba(255,119,0,0.05) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}>
            <ReceiptIcon sx={{ 
              fontSize: { xs: 48, sm: 60, md: 72 }, 
              color: '#FF7700',
              opacity: 0.7
            }} />
          </Box>

          {/* Main Message */}
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight={600} 
            sx={{ 
              color: '#2E4057',
              mb: 1
            }}
          >
            No Invoices Available
          </Typography>

          {/* Subtitle */}
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              maxWidth: { xs: '100%', sm: '500px', md: '600px' },
              mb: 3,
              lineHeight: 1.6
            }}
          >
            Currently, there are no invoices generated for your properties. 
            Invoices are typically created based on your reservations and bookings.
          </Typography>

          {/* Action Cards */}
          <Grid container spacing={3} sx={{ 
            maxWidth: { xs: '100%', sm: '600px', md: '800px' },
            mt: 2
          }}>
            {/* Check Reservations Card */}
            <Grid item xs={12} sm={12}>
              <StyledCard sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                }
              }}
              onClick={() => setShowReservationDetails(true)}
              >
                <CalendarIcon sx={{ 
                  fontSize: { xs: 40, sm: 48 }, 
                  color: '#FF7700',
                  mb: 2
                }} />
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: '#2E4057' }}>
                  View Reservations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Check your reservation details and booking information
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<CalendarIcon />}
                  sx={{
                    borderColor: '#FF7700',
                    color: '#FF7700',
                    borderRadius: 3,
                    '&:hover': {
                      background: 'rgba(255, 119, 0, 0.1)',
                      borderColor: '#E56900',
                    }
                  }}
                >
                  View Reservations
                </Button>
              </StyledCard>
            </Grid>
          </Grid>

          {/* Additional Info */}
          <Box sx={{ 
            mt: 4,
            p: 3,
            background: 'rgba(255, 119, 0, 0.05)',
            borderRadius: 3,
            border: '1px solid rgba(255, 119, 0, 0.2)',
            maxWidth: { xs: '100%', sm: '600px' }
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              <strong>Note:</strong> Invoices are automatically generated based on your reservations. 
              If you have reservations but no invoices, please contact support.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PayInvoice;
